import { Link, type LinkComponentProps } from '@tanstack/react-router';
import {
  DirectoryCard as SharedDirectoryCard,
  type DirectoryEntryProps as SharedDirectoryEntryProps,
} from '~ui';
import { type EntityLogoProps, resolveEntityLogo } from './EntityLogo';

/**
 * The directory card and row (decision D-3) live in `internal/ui`, so a fragment can render the
 * same entry. These wrappers keep the portal's typed destination — `to`/`params`/`search`, checked
 * against the route tree — and its asset-resolving logo, and hand the shared component the router
 * `Link` as the anchor host.
 */

/**
 * @desc The typed destination a directory entry links to: the same `to`/`params`/`search` a router
 * link takes, and `resetScroll: false` for an entry that is an in-page control (the brand row).
 */
export type DirectoryLinkProps = Pick<
  LinkComponentProps<'a'>,
  'to' | 'params' | 'search' | 'resetScroll'
>;

export type DirectoryEntryProps = DirectoryLinkProps &
  Omit<SharedDirectoryEntryProps, 'linkComponent' | 'linkProps' | 'logo'> & {
    logo: EntityLogoProps;
  };

export type DirectoryCardProps = DirectoryEntryProps & {
  /**
   * @desc The entry that is the current page (the brand carousel's own brand, C10): filled
   * Primary 20, no arrow and no link — `aria-current="page"` instead.
   */
  current?: boolean;
};

/** @desc The shared entry's props from the portal's: router link host, resolved logo. */
export const sharedEntryProps = ({
  to,
  params,
  search,
  resetScroll,
  logo,
  ...entry
}: DirectoryEntryProps) => ({
  ...entry,
  logo: resolveEntityLogo(logo),
  linkComponent: Link,
  linkProps: { to, params, search, ...(resetScroll === undefined ? {} : { resetScroll }) },
});

/** @desc Figma's directory card (`301:58186`) as a typed router link. */
export function DirectoryCard({ current, ...entry }: DirectoryCardProps) {
  return <SharedDirectoryCard {...sharedEntryProps(entry)} current={current} />;
}
